import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AgentMessage } from "./agentApi";
import { genuiToolCallPart } from "./genui/part";
import { MESSAGE_BACKEND_ID_KEY } from "./messageIdentity";

/**
 * Convert a history message to a thread message, preserving the backend id in
 * `metadata.custom` (the field the streaming transport also writes) and any persisted
 * rating in `metadata.submittedFeedback`.
 *
 * A `ui_component` entry is a display component the agent rendered (itself or through a
 * sub-agent) — replayed as an assistant message carrying the same genui part the live
 * stream builds, so a reloaded transcript shows the card rather than losing it.
 */
export function toThreadMessage(raw: AgentMessage): ThreadMessageLike {
  const metadata: ThreadMessageLike["metadata"] = {
    custom: raw.id ? { [MESSAGE_BACKEND_ID_KEY]: raw.id } : undefined,
    submittedFeedback: raw.feedback ? { type: raw.feedback.type } : undefined,
  };
  if (raw.role === "ui_component") {
    return {
      // The component instance id is this message's only stable identity — history
      // entries for components carry no message id of their own.
      id: raw.component_id,
      role: "assistant",
      content:
        raw.component_id && raw.component
          ? [
              genuiToolCallPart({
                componentId: raw.component_id,
                component: raw.component,
                props: raw.props,
                schemaVersion: raw.schema_version,
              }),
            ]
          : [],
      createdAt: raw.ts ? new Date(raw.ts) : undefined,
      metadata,
    };
  }
  return {
    // Synthesised entries (e.g. `component_prompt`) send an explicit null id; normalise it
    // to undefined so assistant-ui sees "no id" rather than a null it would key on.
    id: raw.id ?? undefined,
    role: raw.role,
    content: raw.content ?? "",
    createdAt: raw.ts ? new Date(raw.ts) : undefined,
    metadata,
  };
}
